import { useMemo } from "react";
import { Link } from "@reach/router";
import { useTranslation } from "react-i18next";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { ReactComponent as IconCopy } from "bootstrap-icons/icons/copy.svg";

// Recuadro de cartilla. Entre marcas cambian sólo el logo y el color del borde; el
// comportamiento y los elementos son los mismos: ícono + link + botón de copiar,
// donde el copiar puede o no estar.
//
// - Todo el recuadro es clickeable: `stretched-link` estira el área del <a> a toda la
//   caja, y el botón de copiar queda por encima (z-index) con un hit area más grande
//   que el ícono, para poder copiar sin abrir el link.
// - Sin `url`: si viene `to`, el recuadro entero navega ahí (ej. "Configurar Crontu");
//   si no, se muestra sólo el texto.
//
// No se usa directo: va a través de los presets por marca — Components/Crontu,
// Components/DrApp.
//
// props:
//   logo, color → la marca; lo único que distingue a un preset de otro
//   background  → relleno del recuadro; sin él queda el gris de `bg-light`
//   border      → color del borde; por defecto el de la marca. `false` para los
//                 recuadros que en el boceto van sin borde
//   alt         → texto alternativo del logo
//   url         → link de la cartilla
//   label       → texto a mostrar; por defecto el pathname de `url`
//   copy        → mostrar u ocultar el botón de copiar (sólo aplica con `url`)
//   to, onClick → destino interno y handler cuando no hay `url`
//   size        → lado en px del logo y del ícono de copiar
const Directory = ({
  logo,
  color,
  background,
  border,
  alt = "",
  url,
  label,
  copy = true,
  to,
  onClick,
  size = "16",
  className = "",
}) => {
  const { t } = useTranslation();

  // Se muestra el path y no la URL completa: es lo que identifica a la cartilla.
  const text = useMemo(() => {
    if (label) return label;
    if (!url) return "";
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  }, [label, url]);

  // Recuadro de 40px de alto, como en el boceto: padding 8/16 y radio de 6px (`rounded`).
  // Sin `background` se mantiene el gris de `bg-light` de siempre.
  // `border={false}` deja el recuadro sin borde: no alcanza con pasar "transparent",
  // porque el ancho del borde sigue ocupando lugar y desalinea la caja.
  const bordered = border !== false;
  const box = `d-flex align-items-center gap-2 ${bordered ? "border" : ""} rounded px-3 py-2 ${
    background ? "" : "bg-light"
  } ${className}`;
  // El color va por la custom property y no por `borderColor`: la utilidad `.border` de
  // Bootstrap declara el shorthand con `!important`, así que le gana a cualquier
  // `border-color` inline. Pisando `--bs-border-color` se cambia el valor que esa misma
  // regla resuelve, y sí toma.
  const boxStyle = {
    backgroundColor: background,
    ...(bordered ? { "--bs-border-color": border || color } : {}),
  };

  const icon = (
    <img
      src={logo}
      alt={alt}
      width={size}
      height={size}
      className="flex-shrink-0"
      style={{ objectFit: "contain" }}
    />
  );

  if (!url) {
    if (!to) {
      return (
        <div className={box} style={boxStyle}>
          {icon}
          {/* Fix responsive (2026-08-14): text-truncate no recorta nada si
              el flex item (hijo directo de box, que es d-flex) no puede
              achicarse por debajo de su contenido — sin minWidth:0 un
              label/URL largo desborda en vez de truncar con "…". */}
          <span
            className="small text-muted text-truncate"
            style={{ minWidth: 0 }}
            data-tooltip-id="app-tooltip"
            data-tooltip-content={text}
          >
            {text}
          </span>
        </div>
      );
    }

    // El logo va pegado al texto y el conjunto centrado en el recuadro, como en el
    // boceto: `flex-fill` deja que el bloque ocupe el ancho y `justify-content-center`
    // lo centra aunque el texto sea corto.
    return (
      <Link
        to={to}
        onClick={onClick}
        className={`${box} justify-content-center text-decoration-none`}
        style={boxStyle}
      >
        {icon}
        <span className="small fw-bold text-truncate" style={{ color, minWidth: 0 }}>
          {text}
        </span>
      </Link>
    );
  }

  return (
    <div className={`position-relative ${box}`} style={boxStyle}>
      {icon}
      {/* El link se centra con el resto del contenido; `d-block` es lo que hace efectivo
          al `text-truncate` cuando la URL no entra. */}
      <div className="flex-fill text-center" style={{ minWidth: 0 }}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="small text-truncate d-block stretched-link"
          style={{ color }}
          data-tooltip-id="app-tooltip"
          data-tooltip-content={text}
        >
          {text}
        </a>
      </div>
      {copy && (
        <CopyToClipboard
          text={url}
          // Bug real (auditoría 2026-08-16): onCopy descartaba ambos
          // argumentos que pasa react-copy-to-clipboard (text, result) — un
          // copy fallido (permiso denegado, contexto no seguro, browser
          // viejo) se veía IDÉNTICO a uno exitoso, cero feedback en
          // cualquiera de los dos casos.
          onCopy={(_text, result) => {
            window.notify(
              result
                ? { message: t("copied", "Copiado"), type: "success" }
                : { message: t("copy_error", "No se pudo copiar"), type: "danger", title: t("error") }
            )
          }}
        >
          <button
            className="btn btn-sm btn-link p-0 flex-shrink-0 d-flex align-items-center justify-content-center"
            aria-label={t("copy")}
            data-tooltip-id="app-tooltip"
            data-tooltip-content={t("copy")}
            // z-index por encima del área estirada del link; el mínimo agranda el área
            // clickeable sin agrandar el ícono. El ícono va del color de la marca, como
            // el `content_copy` del boceto.
            style={{ position: "relative", zIndex: 2, minWidth: "1.5rem", minHeight: "1.5rem", color }}
          >
            <IconCopy width={size} height={size} />
          </button>
        </CopyToClipboard>
      )}
    </div>
  );
};

export default Directory;
